
export default function GuidanceComparison() {
  const beginnerGuidance = [
    {
      icon: 'ri-file-list-3-line',
      title: 'Step-by-Step Pricing',
      description: 'Detailed breakdowns of material costs, labor rates, and markup percentages for your specific trade'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'License & Insurance Basics',
      description: 'What permits you need, insurance requirements, and how to stay compliant in your state'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Client Communication Scripts',
      description: 'Templates for estimates, follow-ups, and handling difficult conversations'
    },
    {
      icon: 'ri-error-warning-line',
      title: 'Common Mistake Alerts',
      description: 'Warnings about pricing too low, scope creep, and other rookie pitfalls'
    },
    {
      icon: 'ri-tools-line',
      title: 'Tool & Equipment Lists',
      description: 'Essential gear recommendations with budget-friendly alternatives'
    },
    {
      icon: 'ri-book-open-line',
      title: 'Trade Fundamentals',
      description: 'Industry standards, best practices, and foundational knowledge for your craft'
    }
  ];

  const experiencedGuidance = [
    {
      icon: 'ri-line-chart-line',
      title: 'Profit Optimization',
      description: 'Advanced strategies for increasing margins without losing competitive edge'
    },
    {
      icon: 'ri-team-line',
      title: 'Crew Management',
      description: 'Hiring tips, delegation frameworks, and subcontractor coordination'
    },
    {
      icon: 'ri-building-2-line',
      title: 'Scaling Strategies',
      description: 'When to expand, add services, or specialize based on market demand'
    },
    {
      icon: 'ri-funds-line',
      title: 'Cash Flow Mastery',
      description: 'Managing multiple projects, payment terms, and seasonal fluctuations'
    },
    {
      icon: 'ri-award-line',
      title: 'Premium Positioning',
      description: 'Building reputation, commanding higher rates, and attracting ideal clients'
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: 'Industry Trends',
      description: 'New materials, techniques, and market shifts affecting your trade'
    }
  ];

  return (
    <section className="bg-gradient-to-b from-gray-50 to-white py-24">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-teal-50 text-teal-600 rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
            Adaptive Guidance
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Guidance that grows with you
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            AtlasIQ adapts to your experience level, delivering the right information at the right time
          </p>
        </div>

        {/* Comparison Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Beginner Column */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-8 py-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <i className="ri-seedling-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Just Starting Out</h3>
                  <p className="text-white/80 text-sm">0-2 years in business</p>
                </div>
              </div>
            </div>
            <div className="p-8">
              <div className="space-y-6">
                {beginnerGuidance.map((item, index) => (
                  <div key={index} className="flex gap-4 group">
                    <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-amber-100 transition-colors">
                      <i className={`${item.icon} text-amber-600 text-xl`}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Experienced Column */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 px-8 py-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <i className="ri-trophy-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Established Pro</h3>
                  <p className="text-white/80 text-sm">3+ years in business</p>
                </div>
              </div>
            </div>
            <div className="p-8">
              <div className="space-y-6">
                {experiencedGuidance.map((item, index) => (
                  <div key={index} className="flex gap-4 group">
                    <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-teal-100 transition-colors">
                      <i className={`${item.icon} text-teal-600 text-xl`}></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Note */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 bg-gray-100 rounded-full px-6 py-3">
            <i className="ri-magic-line text-[#ff6b35] text-xl"></i>
            <p className="text-gray-700">
              <span className="font-semibold">Smart transitions:</span> As your business grows, guidance automatically evolves with you
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
